import { useCallback, useEffect, useRef, useState } from "react";
import type { IcmJob } from "@/icm/types";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import { type AiScriptSpreadsheetViewerHandle } from "@/payroll/ai-script/ai-script-spreadsheet-viewer";
import type {
  AiScriptChatSelectionContext,
  IAiScriptEditedCellSelection,
  IAiSpreadsheetCellLocation,
} from "@/payroll/ai-script/types";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
} from "@/payroll/pay-breakdown/types";
import { useManualEditsSidebar } from "@/payroll/pay-breakdown/use-manual-edits-sidebar";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";
import type { PayBreakdownSectionsState } from "./use-pay-breakdown-sections";

type UsePayBreakdownPanelsParams = Pick<
  PayBreakdownPeriodState,
  "output" | "aiScriptRunId"
> &
  Pick<PayBreakdownSectionsState, "activeSection"> & {
    canUseNotes: boolean;
  };

export const usePayBreakdownPanels = ({
  output,
  aiScriptRunId,
  activeSection,
  canUseNotes,
}: UsePayBreakdownPanelsParams) => {
  const [selectedJob, setJob] = useState<IcmJob | null>(null);
  const [isSectionSidebarOpen, setSectionOpen] = useState(false);
  const sectionSidebarCloseRef = useRef<(() => void) | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isExplanationChatOpen, setIsExplanationChatOpen] = useState(false);
  const [isWorkbookExportOpen, setIsWorkbookExportOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [notesOpenRequested, setIsNotesOpen] = useState(false);
  const isNotesOpen = canUseNotes && notesOpenRequested;
  const setSelectedJob = useCallback((job: IcmJob | null) => {
    if (job) setIsNotesOpen(false);
    setJob(job);
  }, []);
  const setSectionSidebarOpen = useCallback((open: boolean) => {
    if (open) setIsNotesOpen(false);
    setSectionOpen(open);
  }, []);
  const {
    isOpen: isManualEditsOpen,
    focusedEdit,
    close: closeManualEdits,
    openList: openManualEditsList,
    openEdit: openManualEdit,
    focusEdit: focusManualEdit,
    showAll: showAllManualEdits,
    handleSelectionChange: handleEditedCellSelectionChange,
  } = useManualEditsSidebar();
  const [isSheetMaximized, setIsSheetMaximized] = useState(false);
  const [selectionContext, setSelectionContext] =
    useState<AiScriptChatSelectionContext | null>(null);
  const spreadsheetViewerRef = useRef<AiScriptSpreadsheetViewerHandle | null>(
    null,
  );

  useEffect(() => {
    if (output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript) return;
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(false);
  }, [output.type]);

  useEffect(() => {
    closeManualEdits();
    setSelectionContext(null);
    setPendingQuestion(null);
  }, [aiScriptRunId, closeManualEdits]);

  const handleOpenExplanationChat = () => {
    setIsNotesOpen(false);
    if (activeSection === PAY_BREAKDOWN_SECTION.packChat) {
      setSelectionContext(null);
    }
    setSelectedJob(null);
    closeManualEdits();
    setIsCommentsOpen(false);
    setIsExplanationChatOpen(true);
  };

  const handleAskAiAboutCell = (context: AiScriptChatSelectionContext) => {
    setSelectionContext(context);
    setPendingQuestion(CELL_FIGURE_QUESTION);
    handleOpenExplanationChat();
  };

  const handleOpenComments = () => {
    setIsNotesOpen(false);
    setSelectedJob(null);
    closeManualEdits();
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(true);
  };

  const handleOpenManualEdits = () => {
    setIsNotesOpen(false);
    setSelectedJob(null);
    setIsCommentsOpen(false);
    setIsExplanationChatOpen(false);
    openManualEditsList();
  };

  const handleViewEditedCell = (selection: IAiScriptEditedCellSelection) => {
    setIsNotesOpen(false);
    setSelectedJob(null);
    setIsExplanationChatOpen(false);
    setIsCommentsOpen(false);
    openManualEdit(selection);
  };

  const handleOpenNotes = () => {
    if (!canUseNotes) return;
    sectionSidebarCloseRef.current?.();
    setSectionSidebarOpen(false);
    setSelectedJob(null);
    closeManualEdits();
    setIsCommentsOpen(false);
    setIsExplanationChatOpen(false);
    setIsNotesOpen(true);
  };

  const handleGoToCell = useCallback((cell: IAiSpreadsheetCellLocation) => {
    spreadsheetViewerRef.current?.goToCell(cell.sheet_id, cell.row, cell.col);
  }, []);

  const isSidebarOpen =
    !!selectedJob ||
    isManualEditsOpen ||
    isCommentsOpen ||
    isNotesOpen ||
    isSectionSidebarOpen;
  const registerSectionSidebarClose = useCallback(
    (handler: (() => void) | null) => {
      sectionSidebarCloseRef.current = handler;
    },
    [],
  );

  return {
    selectedJob,
    setSelectedJob,
    setSectionSidebarOpen,
    sectionSidebarCloseRef,
    pendingQuestion,
    setPendingQuestion,
    isExplanationChatOpen,
    setIsExplanationChatOpen,
    isWorkbookExportOpen,
    setIsWorkbookExportOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    isManualEditsOpen,
    focusedEdit,
    closeManualEdits,
    showAllManualEdits,
    focusManualEdit,
    handleEditedCellSelectionChange,
    isSheetMaximized,
    setIsSheetMaximized,
    selectionContext,
    setSelectionContext,
    spreadsheetViewerRef,
    handleOpenExplanationChat,
    handleAskAiAboutCell,
    handleOpenComments,
    handleOpenManualEdits,
    handleViewEditedCell,
    handleGoToCell,
    isSidebarOpen,
    registerSectionSidebarClose,
    isNotesOpen,
    setIsNotesOpen,
    handleOpenNotes,
  };
};
